import React, { useEffect, useState, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styles from './InsightPage.module.css';
import { LanguageContext } from '../../Component/LanguageContext/LanguageContext';
import useScrollToTopWithDelay from '../../hooks/useScrollToTopWithDelay';
import { FaHeart, FaShareAlt, FaArrowLeft, FaArrowRight } from 'react-icons/fa';
import axios from 'axios';

const marketingBackendUrl = import.meta.env.VITE_MARKETING_BACKEND_URL;

const InsightPage = () => {
  useScrollToTopWithDelay(500);
  const { id } = useParams();
  const [insight, setInsight] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { direction } = useContext(LanguageContext);
  const isRTL = direction === 'rtl';
  const navigate = useNavigate();
  const [wishlistStatus, setWishlistStatus] = useState(false);
  const [showSharePopup, setShowSharePopup] = useState(false);
  const [notification, setNotification] = useState({ show: false, message: '' });

  useEffect(() => {
    const fetchInsight = async () => {
      try {
        const response = await fetch(`${marketingBackendUrl}/maflam/insights/${id}`);
        const data = await response.json();
        setInsight(data.data);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching insight:', error);
        setError('Failed to fetch insight');
        setLoading(false);
      }
    };

    fetchInsight();
  }, [id]);

  useEffect(() => {
    const checkWishlistStatus = async () => {
      try {
        const userEmail = localStorage.getItem("useremailidis");
        if (!userEmail || !insight?._id) return;

        const response = await axios.get(`${marketingBackendUrl}/maflam/blog-wishlist/${userEmail}`);
        
        if (response.data.success) {
          const wishlistBlogs = response.data.data;
          const isInWishlist = wishlistBlogs.some(
            wishlistBlog => wishlistBlog.headingEnglish === insight.heading
          );
          setWishlistStatus(isInWishlist);
        }
      } catch (error) {
        console.error('Error checking wishlist status:', error);
      }
    };

    if (insight) {
      checkWishlistStatus();
    }
  }, [insight]);

  const handleGoBack = () => {
    navigate(-1);
  };

  const handleWishlistToggle = async () => {
    try {
      const userEmail = localStorage.getItem("useremailidis");
      
      if (!userEmail) {
        navigate("/home/login");
        return;
      }

      if (wishlistStatus) {
        const response = await axios.post(`${marketingBackendUrl}/maflam/blog-wishlist/remove-blog`, {
          userEmail,
          headingEnglish: insight.heading
        });

        if (response.data.success) {
          setWishlistStatus(false);
          setNotification({
            show: true,
            message: direction === "rtl" 
              ? 'تمت إزالة المقال من قائمة الرغبات بنجاح'
              : 'Insight removed from wishlist successfully'
          });
          setTimeout(() => {
            setNotification({ show: false, message: '' });
          }, 2000);
        }
      } else {
        const blogDetails = {
          headingEnglish: insight.heading,
          headingArabic: insight.heading,
          subheadingEnglish: insight.subheading,
          subheadingArabic: insight.subheading,
          thumbnail: insight.bannerImage,
          redirectionLink: `/home/insights/${insight._id}`
        };

        const response = await axios.post(`${marketingBackendUrl}/maflam/blog-wishlist`, {
          userEmail,
          blogDetails
        });

        if (response.data.success) {
          setWishlistStatus(true);
          setNotification({
            show: true,
            message: direction === "rtl" 
              ? 'تمت إضافة المقال إلى قائمة الرغبات بنجاح'
              : 'Insight added to wishlist successfully'
          });
          setTimeout(() => {
            setNotification({ show: false, message: '' });
          }, 2000);
        }
      }
    } catch (error) {
      console.error('Error updating wishlist:', error);
      if (error.response?.status === 400) {
        setNotification({
          show: true,
          message: direction === "rtl"
            ? 'المقال موجود بالفعل في قائمة الرغبات'
            : 'Insight already exists in wishlist'
        });
        setTimeout(() => {
          setNotification({ show: false, message: '' });
        }, 2000);
      }
    }
  };

  if (loading) return <div className={styles.blogContainer}>Loading...</div>;
  if (error) return <div className={styles.blogContainer}>Error: {error}</div>;
  if (!insight) return <div className={styles.blogContainer}>Insight not found</div>;

  const formatDate = (date) => {
    const options = { year: 'numeric', day: 'numeric', month: 'long' };
    const formattedDate = new Date(date).toLocaleDateString('en-US', options);
    
    // Get the month in Arabic
    const arabicMonths = [
      'يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو',
      'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر'
    ];
    
    const [month, day, year] = formattedDate.split(' ');
    const arabicMonth = arabicMonths[new Date(date).getMonth()]; // Get the Arabic month

    return `${day} ${arabicMonth} ${year}`; // Return formatted date
  };

  const renderContent = (contentItem, index) => {
    const processText = (text) => {
      const parts = [];
      let currentText = text;
      let lastIndex = 0;

      // Process both link and bold patterns
      while (true) {
        const linkMatch = /itisalink\("([^"]+)"\)havingurl\("([^"]+)"\)/g.exec(currentText);
        const boldMatch = /itisbold\("([^"]+)"\)/g.exec(currentText);

        // If no more matches found, break
        if (!linkMatch && !boldMatch) {
          if (lastIndex < currentText.length) {
            parts.push(currentText.slice(lastIndex));
          }
          break;
        }

        // Find which pattern comes first
        const linkIndex = linkMatch ? linkMatch.index : Infinity;
        const boldIndex = boldMatch ? boldMatch.index : Infinity;
        
        if (linkIndex < boldIndex) {
          // Process link
          if (linkIndex > lastIndex) {
            parts.push(currentText.slice(lastIndex, linkIndex));
          }
          
          parts.push(
            <a 
              key={`link-${linkIndex}`}
              href={linkMatch[2]}
              target="_blank"
              rel="noopener noreferrer"
              style={{ 
                color: 'rgb(57, 255, 251)',
                textDecoration: 'none',
                transition: 'opacity 0.2s ease',
              }}
              onMouseOver={(e) => e.target.style.opacity = '0.8'}
              onMouseOut={(e) => e.target.style.opacity = '1'}
            >
              {linkMatch[1]}
            </a>
          );
          
          lastIndex = linkIndex + linkMatch[0].length;
          currentText = currentText.slice(lastIndex);
          lastIndex = 0;
        } else {
          // Process bold
          if (boldIndex > lastIndex) {
            parts.push(currentText.slice(lastIndex, boldIndex));
          }
          
          parts.push(
            <strong 
              key={`bold-${boldIndex}`}
              style={{ 
                fontWeight: '700'
              }}
            >
              {boldMatch[1]}
            </strong>
          );
          
          lastIndex = boldIndex + boldMatch[0].length;
          currentText = currentText.slice(lastIndex);
          lastIndex = 0;
        }
      }

      return parts.length > 0 ? parts : text;
    };

    switch (contentItem.type) {
      case 'heading':
        return (
          <h2 key={index} className={styles.contentHeading}>
            {processText(contentItem.value)}
          </h2>
        );

      case 'paragraph':
        return (
          <p key={index} className={styles.contentParagraph}>
            {processText(contentItem.value)}
          </p>
        );

      case 'numbered-list':
        return (
          <ol key={index} className={styles.numberedList}>
            {contentItem.value.map((item, listIndex) => (
              <li key={listIndex}>{processText(item)}</li>
            ))}
          </ol>
        );

      case 'image':
        return (
          <div key={index} className={styles.imageWrapper}>
            <img
              src={contentItem.value}
              alt={`Insight content ${index + 1}`}
              className={styles.contentImage}
              loading="lazy"
            />
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div style={{ direction: isRTL ? 'rtl' : 'ltr' }} className={styles.blogContainer}>
      {notification.show && (
        <div className={styles.notification}>
          {notification.message}
        </div>
      )}
      <button style={{position: 'absolute', top: '100px', right: '1rem' , zIndex: '1000' , background:'rgb(57, 255, 251)' , color:'black' , border:'none' , borderRadius:'1.5rem' , padding:'0.5rem 1rem' , cursor:'pointer'}} className={styles.goBackButton} onClick={handleGoBack}>
        {isRTL ? <FaArrowRight style={{ marginLeft: '0.5rem' }} /> : <FaArrowLeft style={{ marginRight: '0.5rem' }} />}
        {isRTL ? 'عودة' : 'Go Back'}
      </button>
      <div className={styles.bannerSection}>
        <img 
          src={insight.bannerImage} 
          alt={insight.heading} 
          className={styles.bannerImage}
        />
        <div className={styles.headerContent}>
          <h1 className={styles.heading}>{insight.heading}</h1>
      
          <div className={styles.metaInfo}>
            <span className={styles.instructor}>{insight.author}</span>
            <span className={styles.writingTime}>{formatDate(insight.createdAt)}</span>
            <div className={styles.actionButtons}>
              <button 
                className={`${styles.wishlistButton} ${wishlistStatus ? styles.inWishlist : ''}`}
                onClick={handleWishlistToggle}
              >
                <FaHeart 
                  style={{ 
                    height: '20px', 
                    width: '20px',
                    fill: wishlistStatus ? 'rgb(57, 255, 251)' : 'white'
                  }} 
                />
              </button>
              <button 
                className={styles.shareButton}
                onClick={() => setShowSharePopup(true)}
              >
                <FaShareAlt 
                  style={{ 
                    height: '20px', 
                    width: '20px',
                    fill: 'white'
                  }} 
                />
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className={styles.content}>
        {insight.content.map((contentItem, index) => 
          renderContent(contentItem, index)
        )}
      </div>

      {showSharePopup && (
        <div className={styles.sharePopup}>
          <div className={styles.sharePopupContent}>
            <p>{isRTL ? 'شارك هذا الرابط:' : 'Share this link:'}</p>
            <input type="text" value={window.location.href} readOnly />
            <button onClick={() => {
              navigator.clipboard.writeText(window.location.href);
              setNotification({
                show: true,
                message: isRTL ? 'تم نسخ الرابط' : 'Link copied'
              });
              setTimeout(() => {
                setNotification({ show: false, message: '' });
              }, 2000);
            }}>
              {isRTL ? 'نسخ الرابط' : 'Copy Link'}
            </button>
            <button onClick={() => setShowSharePopup(false)}>{isRTL ? 'إغلاق' : 'Close'}</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default InsightPage; 